import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { scaleLog } from "d3-scale";
import { useEffect, useRef, useState } from "react";
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import DownloadComponentButton from "../button/DownloadComponentButton";
import { useGetColor } from "./Colors";
import { GRAPH_PALETTE, getGraphColorByStage } from "../../theme/style/graphChartPalette";
import { getOrgTokens } from "../../theme/style/organizationPalette";

const calculateMaxLabelWidth = (labels, font = "14px Poppins") => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = font;
  let maxWidth = 0;
  labels.forEach((label) => {
    const width = context.measureText(label).width;
    if (width > maxWidth) maxWidth = width;
  });
  maxWidth += context.measureText("----").width;
  return Math.ceil(maxWidth);
};
export default function HorizontalBarChart({ data = [{ label: "label", value: 0 }], title, referenceLines = [], isLogarithm = false, xAxisLabel, yAxisLabel, titleFontWeight, showTitleDivider = false }) {
  const [yAxisWidth, setYAxisWidth] = useState(60);
  const [visibility, setVisibility] = useState(false);
  const ref = useRef(null);
  const getColor = useGetColor();
  useEffect(() => {
    const width = calculateMaxLabelWidth(
      data.map((d) => d.label),
      "14px Poppins"
    );
    setYAxisWidth(width);
  }, [data]);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tok = getOrgTokens(isDark);

  return (
    <Box onMouseEnter={() => setVisibility(true)} onMouseLeave={() => setVisibility(false)} sx={{ width: "100%", position: "relative" }}>
      <Box sx={{ position: "absolute", top: "5px", right: "5px", zIndex: 10 }}>
        <DownloadComponentButton ref={ref} filename={title + ".png"} visibility={visibility} />
      </Box>
      <Box ref={ref} sx={{
        backgroundColor: "transparent",
        padding: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {title && (
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: titleFontWeight ?? 800,
              color: "text.primary",
              p: 3,
              pb: showTitleDivider ? 2 : 0,
              mb: showTitleDivider ? 1 : 0,
              borderBottom: showTitleDivider ? `1px solid ${tok.border.divider}` : "none",
              letterSpacing: "-0.01em",
              textAlign: "left",
            }}
          >
            {title}
          </Typography>
        )}
        <ResponsiveContainer width="100%" height={Math.max(data.length * 45 + 80, 200)}>
          <BarChart layout="vertical" data={isLogarithm ? data.map((d) => (d.value === 0 ? { ...d, value: null } : d)) : data} margin={{ left: 10, right: 30, bottom: 20 }}>
            <YAxis
              dataKey="label"
              type="category"
              tick={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.standard, fill: isDark ? GRAPH_PALETTE.axes.darkMode.tick : GRAPH_PALETTE.axes.lightMode.tick }}
              width={yAxisWidth}
              axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
              tickLine={false}
              label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', offset: 0, style: { textAnchor: 'middle', fill: isDark ? '#94a3b8' : '#64748b', fontSize: 12, fontWeight: 700 } } : null}
            />
            <XAxis
              dataKey={"value"}
              type="number"
              tick={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.standard, fill: isDark ? GRAPH_PALETTE.axes.darkMode.tick : GRAPH_PALETTE.axes.lightMode.tick }}
              tickFormatter={(val) => (val % 1 !== 0 ? val.toFixed(2) : val.toLocaleString())}
              axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
              tickLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line }}
              scale={isLogarithm ? scaleLog() : undefined}
              domain={[isLogarithm ? 1 : 0, (dataMax) => Math.max(dataMax * 1.1, ...referenceLines.map((r) => r.value))]}
              label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -10, style: { fill: isDark ? GRAPH_PALETTE.axes.darkMode.tick : GRAPH_PALETTE.axes.lightMode.tick, fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.mainAxisTitle, fontWeight: GRAPH_PALETTE.font.weights.bold } } : null}
            />
            <Tooltip
              cursor={{ fill: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}
              formatter={(value) => (value % 1 !== 0 ? value.toFixed(2) : value.toLocaleString())}
              contentStyle={{
                fontFamily: "Poppins",
                fontSize: "13px",
                backgroundColor: isDark ? '#1e293b' : '#fff',
                borderColor: isDark ? '#334155' : 'rgba(0,0,0,0.1)',
                color: isDark ? '#f8fafc' : '#0f172a',
                borderRadius: "12px",
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                border: '1px solid',
              }}
              itemStyle={{ color: isDark ? '#f8fafc' : '#0f172a' }}
            />
            {referenceLines.map((r, i) => (
              <ReferenceLine key={i} x={r.value} stroke={r.color} strokeWidth={2} strokeDasharray="3 3" />
            ))}
            <Bar dataKey="value" radius={[0, 6, 6, 0]} isAnimationActive={true} barSize={18}>
              {data.map((entry, index) => {
                const stageColor = getGraphColorByStage(entry.label);
                const finalColor = stageColor !== GRAPH_PALETTE.lcaStages.Fallback ? stageColor : getColor(index, data.length);
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={finalColor}
                    fillOpacity={isDark ? 0.9 : 0.8}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
